import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import Swal from "sweetalert2";
import axios from "axios";

import DynamicForm from "../../../../../data/Axios/DynamicForm";
import { fetchJsonDynamic } from "../../../../../data/DynamicServiceNew/jsondynamicservice";
import { SDLSUrl } from "../../../../../config";

const base_url = SDLSUrl.uri;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  return {
    Authorization: `Bearer ${token.replace(/"/g, "")}`,
    "Content-Type": "application/json",
  };
};

const Form = (props) => {
  const customerId = parseInt(localStorage.getItem("CustomerId"), 10);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);

  const isEditing = !!props.isEditing;

  const [availableDevices, setAvailableDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDevices() {
      setLoading(true);
      try {
        const variables = isEditing
          ? { customerId }
          : { customerId: null, maziwaStatus: "PENDING_CUSTOMER_MAPPING"};

        const response = await fetchJsonDynamic(
          "deviceRegistry",
          "param",
          variables,
          base_url
        );
        if (response?.results) {
          const parsed = JSON.parse(response.results);
          setAvailableDevices(Array.isArray(parsed) ? parsed : []);
        } else {
          setAvailableDevices([]);
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
        setAvailableDevices([]);
      } finally {
        setLoading(false);
      }
    }
    fetchDevices();
  }, [isEditing]);

  const deviceOptions = availableDevices.map((d) => ({
    value: d.Imei1,
    label: `${d.Imei1}${d.ModelName ? ` — ${d.ModelName}` : ""}`,
  }));

  const initialValues = {
    imei1: props.data ? props.data.Imei1 : "",
  };

  const fields = [
    {
      name: "imei1",
      label: isEditing ? "Linked Device to Remove" : "Device (Primary IMEI)",
      type: "select",
      options: deviceOptions,
      gridColumn: "span 4",
      required: true,
    },
  ];

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    const mutationField = isEditing
      ? "unMapDeviceToCustomer"
      : "mapDeviceToCustomer";

    try {
      const headers = getAuthHeaders();
      const mutation = {
        query: `
          mutation {
            ${mutationField}(imei1: "${values.imei1}", customerId: ${customerId}) {
              results
              error
            }
          }
        `,
      };
      const response = await axios.post(base_url, mutation, { headers });
      const data = response?.data?.data?.[mutationField];

      if (data?.results) {
        props.onClose();
        props.onAction();
        Swal.fire({
          icon: "success",
          title: isEditing ? "Device Unlinked!" : "Device Linked!",
          text: isEditing
            ? `Device ${values.imei1} has been removed from this customer.`
            : `Device ${values.imei1} has been linked to this customer.`,
        });
        setSubmitting(false);
        return;
      }

      if (data?.error) {
        let errorMessage = "An unknown error occurred.";
        try {
          const errorObj =
            typeof data.error === "string" ? JSON.parse(data.error) : data.error;
          if (typeof errorObj === "string") {
            errorMessage = errorObj;
          } else if (errorObj?.error) {
            errorMessage = errorObj.error;
          } else if (errorObj?.Error) {
            errorMessage =
              errorObj.Error.match(/System\.Exception: (.*?)\./)?.[1] ||
              errorObj.Error;
          }
        } catch (err) {
          errorMessage = data.error;
        }

        if (errorMessage.toLowerCase().includes("already")) {
          setFieldError("imei1", errorMessage);
          setSubmitting(false);
          return;
        }

        Swal.fire({
          icon: "error",
          title: "Error!",
          text:
            errorMessage ||
            (isEditing ? "Failed to remove device." : "Failed to link device."),
        });
      }
    } catch (error) {
      console.error(isEditing ? "Unlinking error:" : "Linking error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (deviceOptions.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="textSecondary">
          {isEditing
            ? "This customer has no linked devices to remove."
            : "No unassigned devices found in the Device Registry. Register a device there before linking it to a customer."}
        </Typography>
      </Box>
    );
  }

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      onSubmit={handleSubmit}
      title={isEditing ? "Remove Device" : "Link Device"}
      subtitle={
        isEditing
          ? "Select a linked device to remove from this customer"
          : "Select an available device to link to this customer"
      }
    />
  );
};

export default Form;